/**
 * Blocos recharts da Análise de Atrasos: Pareto (B3), Tendência (C1) e
 * Custo por categoria (C3-esquerda). Todos reagem à lente e ao recorte
 * (nenhum gráfico estático) e usam a MESMA paleta COR_CATEGORIA dos
 * demais blocos.
 */
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatarReais } from "../../../services/financeiro";
import { formatarDataCurta } from "../../../utils/dataHora";
import { COR_CATEGORIA, LABEL_CATEGORIA, ORDEM_CATEGORIAS } from "../categorias6m";
import { NotaDiscreta, fmtNum } from "../_shared/ui";

const TICK = { fontSize: 10, fill: "var(--ink-3)" };

function truncar(texto, max = 18) {
  const s = String(texto || "");
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Extrai o datum do evento de clique do recharts (2.x/3.x defensivo). */
function datumDoClique(evento) {
  return evento?.payload || evento || null;
}

// ---------------------------------------------------------------------------
// B3 — Pareto 80/20 (ComposedChart: barras peso + linha % acumulado)
// ---------------------------------------------------------------------------

export function ParetoBloco({
  itens, ehTempo, categoriaAtiva, onCategoriaClick, granularidade,
}) {
  const rotuloPeso = ehTempo ? "min" : "ocorrências";
  const comPeso = itens.filter((i) => i.peso > 0);

  const insight = useMemo(() => {
    if (comPeso.length === 0) return "Sem ocorrências no recorte.";
    const idx80 = comPeso.findIndex((i) => i.pct_acumulado >= 80);
    const n = idx80 === -1 ? comPeso.length : idx80 + 1;
    const alvo = comPeso[Math.min(n - 1, comPeso.length - 1)];
    const oQue =
      granularidade === "categoria"
        ? "categorias"
        : ehTempo
          ? "causas"
          : "tipos de defeito";
    const doQue = ehTempo ? "do atraso" : "das reprovações";
    return `${n} de ${comPeso.length} ${oQue} respondem por ${fmtNum(alvo.pct_acumulado)}% ${doQue}.`;
  }, [comPeso, ehTempo, granularidade]);

  if (comPeso.length === 0) {
    return <NotaDiscreta>{insight}</NotaDiscreta>;
  }

  return (
    <>
      <NotaDiscreta style={{ margin: "0 0 8px", color: "var(--ink-2)" }}>
        {insight}
      </NotaDiscreta>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={comPeso}
          margin={{ top: 6, right: 6, left: 0, bottom: 6 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
          <XAxis
            dataKey="nome"
            tick={TICK}
            interval={0}
            angle={-18}
            height={54}
            textAnchor="end"
            tickFormatter={(v) => truncar(v)}
          />
          <YAxis yAxisId="peso" tick={TICK} width={40} allowDecimals={false} />
          <YAxis
            yAxisId="pct"
            orientation="right"
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={TICK}
            width={38}
          />
          <Tooltip
            formatter={(valor, nome) =>
              nome === "% acumulado"
                ? [`${fmtNum(valor)}%`, nome]
                : [`${fmtNum(valor, 0)} ${rotuloPeso}`, "Peso"]
            }
          />
          <ReferenceLine
            yAxisId="pct"
            y={80}
            stroke="var(--warning-solid)"
            strokeDasharray="4 4"
          />
          <Bar
            yAxisId="peso"
            dataKey="peso"
            name="Peso"
            radius={[3, 3, 0, 0]}
            onClick={(e) => {
              const d = datumDoClique(e);
              if (d?.categoria && onCategoriaClick) onCategoriaClick(d.categoria);
            }}
          >
            {comPeso.map((d, i) => (
              <Cell
                key={`${d.nome}-${i}`}
                cursor={d.categoria ? "pointer" : undefined}
                fill={
                  d.categoria
                    ? COR_CATEGORIA[d.categoria] || "var(--info-solid)"
                    : "var(--info-solid)"
                }
                opacity={
                  categoriaAtiva && d.categoria && d.categoria !== categoriaAtiva
                    ? 0.35
                    : 1
                }
              />
            ))}
          </Bar>
          <Line
            yAxisId="pct"
            dataKey="pct_acumulado"
            name="% acumulado"
            stroke="var(--ink-2)"
            strokeWidth={2}
            dot={{ r: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
}

// ---------------------------------------------------------------------------
// C1 — Tendência temporal (barras empilhadas 6M + linha total + média)
// ---------------------------------------------------------------------------

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Preenche a escala do eixo com zeros (o backend só materializa buckets
 * com dado). Limite defensivo de 400 pontos. */
export function preencherBuckets(tendencia, periodo) {
  const bucket = tendencia?.bucket || "dia";
  const existentes = new Map(
    (tendencia?.buckets || []).map((b) => [b.inicio, b]),
  );
  if (!periodo?.inicio || !periodo?.fim) return [];

  const inicio = new Date(periodo.inicio);
  const fim = new Date(periodo.fim);
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime())) return [];

  const cursor = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
  if (bucket === "semana") {
    cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
  }
  const fimDia = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate());
  const passo = bucket === "semana" ? 7 : 1;

  const resultado = [];
  while (cursor <= fimDia && resultado.length < 400) {
    const iso = isoLocal(cursor);
    const existente = existentes.get(iso);
    const por = existente?.por_categoria || {};
    const linha = {
      inicio: iso,
      rotulo: formatarDataCurta(iso),
      total: Number(existente?.total || 0),
    };
    for (const codigo of ORDEM_CATEGORIAS) linha[codigo] = Number(por[codigo] || 0);
    resultado.push(linha);
    cursor.setDate(cursor.getDate() + passo);
  }
  return resultado;
}

export function TendenciaBloco({ tendencia, periodo, ehTempo, onBucketClick }) {
  const buckets = useMemo(
    () => preencherBuckets(tendencia, periodo),
    [tendencia, periodo],
  );

  const { media, insight, subiu } = useMemo(() => {
    const totais = buckets.map((b) => b.total);
    const soma = totais.reduce((a, v) => a + v, 0);
    const m = totais.length ? soma / totais.length : 0;
    const metade = Math.floor(totais.length / 2);
    const primeira = totais.slice(0, metade).reduce((a, v) => a + v, 0);
    const segunda = totais.slice(metade).reduce((a, v) => a + v, 0);
    if (primeira <= 0) {
      return { media: m, insight: null, subiu: null };
    }
    const variacao = (100 * (segunda - primeira)) / primeira;
    const alvo = ehTempo ? "atraso" : "não-conformidades";
    // Primeira metade quase vazia infla o % — acima de 300%, a razão (×)
    // comunica melhor que uma porcentagem absurda.
    const texto =
      Math.abs(variacao) > 300
        ? `${alvo} ${fmtNum(segunda / primeira, 1)}× a primeira metade do período`
        : `${alvo} ${variacao > 0 ? "↑" : "↓"} ${fmtNum(Math.abs(variacao), 0)}% vs primeira metade do período`;
    return { media: m, subiu: variacao > 0, insight: texto };
  }, [buckets, ehTempo]);

  if (buckets.length === 0 || buckets.every((b) => b.total === 0)) {
    return <NotaDiscreta>Sem ocorrências no recorte para desenhar a tendência.</NotaDiscreta>;
  }

  return (
    <>
      {insight && (
        <NotaDiscreta
          style={{
            margin: "0 0 8px",
            color: subiu ? "var(--danger-text)" : "var(--success-text)",
          }}
        >
          {insight}
        </NotaDiscreta>
      )}
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={buckets} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
          <XAxis dataKey="rotulo" tick={TICK} interval="preserveStartEnd" />
          <YAxis tick={TICK} width={40} allowDecimals={false} />
          <Tooltip
            formatter={(valor, nome) => [
              `${fmtNum(valor, 0)}${ehTempo ? " min" : ""}`,
              nome,
            ]}
          />
          <ReferenceLine
            y={media}
            stroke="var(--ink-3)"
            strokeDasharray="4 4"
            label={{
              value: "média",
              fontSize: 10,
              fill: "var(--ink-3)",
              position: "insideTopRight",
            }}
          />
          {ORDEM_CATEGORIAS.map((codigo) => (
            <Bar
              key={codigo}
              dataKey={codigo}
              stackId="categorias"
              name={LABEL_CATEGORIA[codigo]}
              fill={COR_CATEGORIA[codigo]}
              cursor={onBucketClick ? "pointer" : undefined}
              onClick={(e) => {
                const d = datumDoClique(e);
                if (d?.inicio && onBucketClick) {
                  onBucketClick(d.inicio, tendencia?.bucket || "dia");
                }
              }}
            />
          ))}
          <Line
            dataKey="total"
            name="Total"
            stroke="var(--ink-2)"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
}

// ---------------------------------------------------------------------------
// C3 (esquerda) — Custo por categoria (BarChart horizontal)
// ---------------------------------------------------------------------------

export function CustoBloco({ custoPorCategoria, ehTempo, parametros }) {
  const dados = (custoPorCategoria || []).map((c) => ({
    categoria: c.categoria,
    label: c.label || LABEL_CATEGORIA[c.categoria] || c.categoria,
    custo_rs: Number(c.custo_rs || 0),
    horas: Number(ehTempo ? c.horas_perdidas || 0 : c.horas_estimadas || 0),
  }));
  const total = dados.reduce((a, d) => a + d.custo_rs, 0);
  const horasTotais = dados.reduce((a, d) => a + d.horas, 0);

  return (
    <>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 18,
          fontWeight: 700,
          color: "var(--ink)",
          marginBottom: 8,
        }}
      >
        {formatarReais(total)}
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: "var(--ink-3)",
            marginLeft: 8,
          }}
        >
          {fmtNum(horasTotais)} h {ehTempo ? "perdidas" : "estimadas"}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          layout="vertical"
          data={dados}
          margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" horizontal={false} />
          <XAxis
            type="number"
            tick={TICK}
            tickFormatter={(v) =>
              v >= 1000 ? `${Math.round(v / 1000)}k` : `${Math.round(v)}`
            }
          />
          <YAxis
            type="category"
            dataKey="label"
            width={96}
            tick={{ fontSize: 11, fill: "var(--ink-2)" }}
          />
          <Tooltip formatter={(v) => [formatarReais(v), "Custo"]} />
          <Bar dataKey="custo_rs" radius={[0, 3, 3, 0]}>
            {dados.map((d) => (
              <Cell key={d.categoria} fill={COR_CATEGORIA[d.categoria]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <NotaDiscreta>
        {ehTempo
          ? `custo-hora R$ ${fmtNum(parametros?.custo_hora ?? 0, 2)}`
          : `custo-hora de retrabalho R$ ${fmtNum(parametros?.custo_hora_retrabalho ?? 0, 2)} · horas estimadas por categoria (Config 8.5.4)`}
        {" — parâmetro da unidade, read-only aqui."}
      </NotaDiscreta>
    </>
  );
}
